import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LoaderCircle, Save } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { useAuthStore } from "../../context/AuthContext";
import { hasPermission } from "../../lib/utils/permissions";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";

const EMPTY_FORM = { title: "", image: null, pdf: null };

const CareerAssetFormPage = ({ config, mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [existingPdfUrl, setExistingPdfUrl] = useState("");

  const isEditMode = mode === "edit";
  const isAdmin = hasPermission(user, "career", isEditMode ? "edit" : "create");
  const pageTitle = useMemo(() => (isEditMode ? `Edit ${config.singular}` : `Create ${config.singular}`), [config.singular, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !isAdmin) {
      setIsPageLoading(false);
      return;
    }

    const fetchItem = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`${config.apiBase}/${id}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast(`${config.singular} not found`);
          navigate(config.listPath);
          return;
        }
        setForm({ title: item.title || "", image: null, pdf: null });
        setImagePreviewUrl(item.image_url || "");
        setExistingPdfUrl(item.pdf_url || "");
      } catch (err) {
        showErrorToast(err.response?.data?.message || `Failed to load ${config.singular.toLowerCase()}`);
        navigate(config.listPath);
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchItem();
  }, [config.apiBase, config.listPath, config.singular, id, isAdmin, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.title.trim()) nextErrors.title = "Title is required.";
    if (!isEditMode && !form.image) nextErrors.image = "Image is required.";
    if (!isEditMode && !form.pdf) nextErrors.pdf = "PDF is required.";
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("title", form.title.trim());
      if (form.image) payload.append("image", form.image);
      if (form.pdf) payload.append("pdf", form.pdf);

      if (isEditMode) {
        await apiClient.put(`${config.apiBase}/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast(`${config.singular} updated successfully`);
      } else {
        await apiClient.post(config.apiBase, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast(`${config.singular} created successfully`);
      }

      navigate(config.listPath);
    } catch (err) {
      showErrorToast(err.response?.data?.message || `Unable to save ${config.singular.toLowerCase()}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
        <p className="mt-2 text-sm text-slate-500">This section is only available for admin users.</p>
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading {config.singular.toLowerCase()} editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate(config.listPath)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to {config.plural}
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSave} className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <Input value={form.title} onChange={(event) => handleInputChange("title", event.target.value)} className={`rounded-lg ${formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.title}</FieldError>
            </div>
            <div className="space-y-2">
              <InlineFormFileUpload label="Image" value={form.image} onChange={(file) => handleInputChange("image", file)} existingFiles={imagePreviewUrl ? [imagePreviewUrl] : []} accept={IMAGE_TYPES} maxSizeBytes={10 * 1024 * 1024} required={!isEditMode} error={formErrors.image} helperText="JPG, PNG, GIF or WEBP — maximum 10 MB" />
              <ImageUploadGuidance requirementKey="careerAsset" file={form.image} src={imagePreviewUrl} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <InlineFormFileUpload label="PDF" value={form.pdf} onChange={(file) => handleInputChange("pdf", file)} existingFiles={existingPdfUrl ? [existingPdfUrl] : []} accept={["application/pdf"]} maxSizeBytes={10 * 1024 * 1024} required={!isEditMode} error={formErrors.pdf} helperText="PDF — maximum 10 MB" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(config.listPath)}>Cancel</Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? `Update ${config.singular}` : `Create ${config.singular}`}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CareerAssetFormPage;
